import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import toast from "react-hot-toast";
import { useWallet } from "../context/WalletContext";

const MiningAdmin = () => {
    const { miningContract, readOnlyMiningContract, account } = useWallet();
    const initKeyRef = useRef("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    const [minDeposit, setMinDeposit] = useState("");
    const [maxDeposit, setMaxDeposit] = useState("");
    const [currentMinDeposit, setCurrentMinDeposit] = useState("0");
    const [currentMaxDeposit, setCurrentMaxDeposit] = useState("0");

    const [nodeUserAddress, setNodeUserAddress] = useState("");
    const [nodeLevel, setNodeLevel] = useState("0");
    const [nodeExpireMonths, setNodeExpireMonths] = useState("1");
    const [lastNodeConfig, setLastNodeConfig] = useState(null);

    const [emptyOrderAddress, setEmptyOrderAddress] = useState("");
    const [emptyOrderAmount, setEmptyOrderAmount] = useState("");
    const [lastEmptyOrderConfig, setLastEmptyOrderConfig] = useState(null);

    const [currentRateStages, setCurrentRateStages] = useState([]);
    const [selectedRateStageIndex, setSelectedRateStageIndex] = useState("0");
    const [selectedRateDayThreshold, setSelectedRateDayThreshold] = useState("");
    const [selectedRateCurrentDaily, setSelectedRateCurrentDaily] = useState("");
    const [selectedRateNewDaily, setSelectedRateNewDaily] = useState("");
    const [lmlUserAddress, setLmlUserAddress] = useState("");
    const [lmlUserAmount, setLmlUserAmount] = useState("");
    const [lastLmlUserConfig, setLastLmlUserConfig] = useState(null);
    const [specialRoleAddress, setSpecialRoleAddress] = useState("");
    const [specialRoleEnabled, setSpecialRoleEnabled] = useState("true");
    const [lastSpecialRoleConfig, setLastSpecialRoleConfig] = useState(null);

    const [newAdminAddress, setNewAdminAddress] = useState("");
    const [adminList, setAdminList] = useState([]);

    useEffect(() => {
        if (!readOnlyMiningContract || !account) {
            initKeyRef.current = "";
            return;
        }
        const key = `${readOnlyMiningContract.target || readOnlyMiningContract.address || "mining"}-${account.toLowerCase()}`;
        if (initKeyRef.current === key) return;
        initKeyRef.current = key;
        checkAdmin();
    }, [readOnlyMiningContract, account]);

    const checkAdmin = async () => {
        try {
            setLoading(true);
            const adminRole = await readOnlyMiningContract.ADMIN_ROLE();
            const hasAdminRole = await readOnlyMiningContract.hasRole(adminRole, account);
            setIsAdmin(hasAdminRole);
            if (hasAdminRole) {
                loadConfigData();
                loadRateStages();
                loadAdminList();
            }
        } catch (error) {
            console.error("Error checking admin status:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadConfigData = async () => {
        if (!readOnlyMiningContract) return;
        try {
            const [min, max] = await Promise.all([
                readOnlyMiningContract.minDeposit(),
                readOnlyMiningContract.maxDeposit(),
            ]);
            setCurrentMinDeposit(ethers.formatEther(min));
            setCurrentMaxDeposit(ethers.formatEther(max));
        } catch (error) {
            console.error("Error loading mining config:", error);
        }
    };

    const loadRateStages = async () => {
        if (!readOnlyMiningContract) return;
        try {
            const stages = [];
            // 合约是固定数组 RateStage[4]，直接读取索引 0~3
            for (let i = 0; i < 4; i++) {
                const stage = await readOnlyMiningContract.rateStages(i);
                const dayThreshold = Number(stage.dayThreshold ?? stage[0] ?? 0);
                const hourlyRate = Number(stage.hourlyRate ?? stage[1] ?? 0);
                stages.push({
                    index: i,
                    dayThreshold,
                    hourlyRate,
                    dailyRate: getDailyRateFromHourly(hourlyRate),
                });
            }
            setCurrentRateStages(stages);
            if (stages.length > 0) {
                const current = stages.find((s) => s.index === Number(selectedRateStageIndex)) || stages[0];
                setSelectedRateStageIndex(String(current.index));
                setSelectedRateDayThreshold(String(current.dayThreshold));
                setSelectedRateCurrentDaily(String(current.dailyRate));
                setSelectedRateNewDaily("");
            } else {
                setSelectedRateDayThreshold("");
                setSelectedRateCurrentDaily("");
                setSelectedRateNewDaily("");
            }
        } catch (error) {
            console.error("Error loading rate stages:", error);
        }
    };

    const loadAdminList = async () => {
        if (!readOnlyMiningContract) return;
        try {
            const adminRole = await readOnlyMiningContract.ADMIN_ROLE();
            const candidates = [account, newAdminAddress].filter((addr) => addr && ethers.isAddress(addr));
            const uniqCandidates = Array.from(new Set(candidates.map((addr) => addr.toLowerCase())));
            const checks = await Promise.all(
                uniqCandidates.map(async (addr) => {
                    try {
                        const has = await readOnlyMiningContract.hasRole(adminRole, addr);
                        return has ? addr : null;
                    } catch {
                        return null;
                    }
                })
            );
            const admins = checks.filter(Boolean);
            setAdminList(admins.length > 0 ? admins : (account ? [account.toLowerCase()] : []));
        } catch (error) {
            console.error("Error loading mining admin list:", error);
            if (account) setAdminList([account.toLowerCase()]);
        }
    };

    const handleTransaction = async (txPromise, toastId, successMsg) => {
        if (!miningContract) {
            toast.error("合约未初始化，请检查钱包连接或网络");
            return false;
        }
        toast.loading("正在准备交易...", { id: toastId });
        try {
            const tx = await txPromise;
            // 如果交易已经处于待处理状态，避免重复发送
            if (tx.wait) {
                toast.loading("交易已提交，等待上链...", { id: toastId });
                const receipt = await tx.wait();
                if (receipt.status === 1) {
                    toast.success(successMsg, { id: toastId });
                    return true;
                } else {
                    toast.error("交易执行失败", { id: toastId });
                    return false;
                }
            }
            toast.success(successMsg, { id: toastId });
            return true;
        } catch (error) {
            console.error("Transaction error:", error);
            let errorMsg = error.reason || error.message || "交易失败";
            if (error.code === "ACTION_REJECTED") {
                errorMsg = "用户取消了交易";
            } else if (error.code === "TRANSACTION_REPLACED") {
                if (error.cancelled) {
                    errorMsg = "交易已取消";
                } else {
                    toast.success(successMsg, { id: toastId });
                    return true;
                }
            } else if (error.code === "NONCE_EXPIRED") {
                errorMsg = "交易Nonce已过期，请重试";
            } else if (error.message && error.message.includes("duplicate call detected")) {
                toast.success(successMsg, { id: toastId });
                return true;
            }
            toast.error(errorMsg, { id: toastId });
            return false;
        }
    };

    const handleSetMinDeposit = async () => {
        if (!minDeposit || Number(minDeposit) <= 0) {
            toast.error("请输入有效的最小充值金额");
            return;
        }
        await handleTransaction(
            miningContract.setMinDeposit(ethers.parseEther(minDeposit)),
            "mindeposit",
            "最小充值金额设置成功"
        );
        loadConfigData();
    };

    const handleSetMaxDeposit = async () => {
        if (!maxDeposit || Number(maxDeposit) <= 0) {
            toast.error("请输入有效的最大充值金额");
            return;
        }
        await handleTransaction(
            miningContract.setMaxDeposit(ethers.parseEther(maxDeposit)),
            "maxdeposit",
            "最大充值金额设置成功"
        );
        loadConfigData();
    };

    const handleSetNodeLevel = async () => {
        if (!ethers.isAddress(nodeUserAddress)) {
            toast.error("请输入有效的用户地址");
            return;
        }
        const now = Math.floor(Date.now() / 1000);
        const monthsInSeconds = Number(nodeExpireMonths) * 30 * 24 * 60 * 60;
        const expireTime = now + monthsInSeconds;

        await handleTransaction(
            miningContract.setNodeLevel(nodeUserAddress, nodeLevel, expireTime),
            "nodelevel",
            "节点等级设置成功"
        );
        setLastNodeConfig({
            address: nodeUserAddress,
            level: nodeLevel,
            expireTime,
        });
    };

    const handleSetEmptyOrder = async () => {
        if (!ethers.isAddress(emptyOrderAddress)) {
            toast.error("请输入有效的用户地址");
            return;
        }
        if (!emptyOrderAmount || Number(emptyOrderAmount) <= 0) {
            toast.error("请输入有效的空单金额");
            return;
        }
        await handleTransaction(
            miningContract.setEmptyOrder(emptyOrderAddress, ethers.parseEther(emptyOrderAmount)),
            "emptyorder",
            "空单金额设置成功"
        );
        setLastEmptyOrderConfig({
            address: emptyOrderAddress,
            amount: emptyOrderAmount,
        });
    };

    const toHourlyRateFromDaily = (dailyRate) => {
        const dailyPercent = Number(dailyRate || 0);
        // hourlyRate 精度：1 = 0.0001%/h
        return Math.max(0, Math.round((dailyPercent / 24) * 10000));
    };

    const handleSetRateStage = async () => {
        if (selectedRateNewDaily === "" || Number(selectedRateNewDaily) < 0) {
            toast.error("请输入有效的新日收益率");
            return;
        }

        const stageIndex = Number(selectedRateStageIndex);
        let dayThreshold = 0;
        try {
            const stage = await readOnlyMiningContract.rateStages(stageIndex);
            dayThreshold = Number(stage.dayThreshold ?? stage[0] ?? 0);
        } catch (error) {
            toast.error("读取当前费率阶段失败");
            return;
        }
        if (!dayThreshold || dayThreshold <= 0) {
            toast.error("当前所选阶段的天数阈值无效");
            return;
        }

        const hourlyRate = toHourlyRateFromDaily(selectedRateNewDaily);
        const success = await handleTransaction(
            miningContract.setRateStage(stageIndex, dayThreshold, hourlyRate),
            `ratestage-${stageIndex}`,
            `阶段 ${stageIndex} 费率已设置`
        );
        if (!success) return;
        loadRateStages();
    };

    const getDailyRateFromHourly = (hourlyRate) => {
        const basisPoints = Number(hourlyRate || 0) / 100;
        const hourlyPercent = basisPoints / 100;
        return (hourlyPercent * 24).toFixed(2);
    };

    const handleSetLMLUser = async () => {
        if (!ethers.isAddress(lmlUserAddress)) {
            toast.error("请输入有效的用户地址");
            return;
        }
        if (!lmlUserAmount || Number(lmlUserAmount) <= 0) {
            toast.error("请输入有效的特殊订单金额");
            return;
        }
        const success = await handleTransaction(
            miningContract.setLMLUser(lmlUserAddress, ethers.parseEther(lmlUserAmount)),
            "lmluser",
            "特殊订单设置成功"
        );
        if (success) {
            setLastLmlUserConfig({
                address: lmlUserAddress,
                amount: lmlUserAmount,
            });
            setLmlUserAddress("");
            setLmlUserAmount("");
        }
    };

    const handleSetSpecialRole = async () => {
        if (!ethers.isAddress(specialRoleAddress)) {
            toast.error("请输入有效的用户地址");
            return;
        }
        const enable = specialRoleEnabled === "true";
        const success = await handleTransaction(
            miningContract.setSpecialRole(specialRoleAddress, enable),
            "specialrole",
            enable ? "10%奖励资格已设置" : "10%奖励资格已取消"
        );
        if (success) {
            setLastSpecialRoleConfig({
                address: specialRoleAddress,
                enabled: enable,
            });
            setSpecialRoleAddress("");
        }
    };

    const handleTriggerSnapshot = async () => {
        if (!window.confirm("确定要触发价格快照吗？")) {
            return;
        }
        await handleTransaction(
            miningContract.triggerSnapshot(),
            "snapshot",
            "价格快照触发成功"
        );
    };

    const handleTransferAdmin = async () => {
        if (!ethers.isAddress(newAdminAddress)) {
            toast.error("请输入有效的管理员地址");
            return;
        }
        if (!window.confirm("确定要转移管理员权限吗？")) {
            return;
        }
        await handleTransaction(
            miningContract.transferOwnership(newAdminAddress),
            "transfer",
            "所有权转移成功"
        );
        loadAdminList();
    };

    const handleRenounceOwnership = async () => {
        if (!window.confirm("确定要丢弃合约权限吗？此操作不可逆！")) {
            return;
        }
        if (!window.confirm("再次确认：丢弃权限后将永久失去管理权限！")) {
            return;
        }
        const signerAddress = await miningContract?.runner?.getAddress?.();
        if (!signerAddress) {
            toast.error("未检测到钱包地址");
            return;
        }
        const adminRole = await miningContract.ADMIN_ROLE();
        const success = await handleTransaction(
            miningContract.renounceRole(adminRole, signerAddress),
            "renounce",
            "管理员权限已丢弃"
        );
        if (success) {
            loadAdminList();
            checkAdmin();
        }
    };

    if (loading) {
        return <div className="loading">加载中...</div>;
    }

    if (!isAdmin) {
        return (
            <div className="card">
                <h2 className="card-title">挖矿管理</h2>
                <p style={ { color: "#ef4444", textAlign: "center", padding: "20px" } }>
                    您没有管理员权限
                </p>
            </div>
        );
    }

    return (
        <div>

            <div className="card">
                <h2 className="card-title">充值限额设置</h2>
                <div className="info-row">
                    <span className="info-label">当前最小充值</span>
                    <span className="info-value">{ currentMinDeposit } BNB</span>
                </div>
                <div className="info-row">
                    <span className="info-label">当前最大充值</span>
                    <span className="info-value">{ currentMaxDeposit } BNB</span>
                </div>
                <div className="form-group" style={ { marginTop: "16px" } }>
                    <label className="form-label">最小充值金额</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ minDeposit }
                        onChange={ (e) => setMinDeposit(e.target.value) }
                        placeholder="0.0"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetMinDeposit } style={ { marginBottom: "12px" } }>
                    设置最小充值
                </button>
                <div className="form-group">
                    <label className="form-label">最大充值金额</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ maxDeposit }
                        onChange={ (e) => setMaxDeposit(e.target.value) }
                        placeholder="0.0"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetMaxDeposit }>
                    设置最大充值
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置节点等级</h2>
                { lastNodeConfig && (
                    <div style={ { marginBottom: "12px" } }>
                        <div className="info-row">
                            <span className="info-label">最近设置地址</span>
                            <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>{ lastNodeConfig.address }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">最近设置等级</span>
                            <span className="info-value">V{ lastNodeConfig.level }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">最近到期时间</span>
                            <span className="info-value">{ new Date(lastNodeConfig.expireTime * 1000).toLocaleString("zh-CN") }</span>
                        </div>
                    </div>
                ) }
                <div className="form-group">
                    <label className="form-label">用户地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ nodeUserAddress }
                        onChange={ (e) => setNodeUserAddress(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">节点等级</label>
                    <select
                        className="form-input"
                        value={ nodeLevel }
                        onChange={ (e) => setNodeLevel(e.target.value) }
                    >
                        <option value="0">V0</option>
                        <option value="1">V1</option>
                        <option value="2">V2</option>
                        <option value="3">V3</option>
                        <option value="4">V4</option>
                        <option value="5">V5</option>
                        <option value="6">V6</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="form-label">过期时间</label>
                    <select
                        className="form-input"
                        value={ nodeExpireMonths }
                        onChange={ (e) => setNodeExpireMonths(e.target.value) }
                    >
                        { Array.from({ length: 24 }, (_, i) => i + 1).map(month => (
                            <option key={ month } value={ month }>{ month }个月</option>
                        )) }
                    </select>
                </div>
                <button className="btn btn-primary" onClick={ handleSetNodeLevel }>
                    设置节点等级
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置空单金额</h2>
                { lastEmptyOrderConfig && (
                    <div style={ { marginBottom: "12px" } }>
                        <div className="info-row">
                            <span className="info-label">最近设置地址</span>
                            <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>{ lastEmptyOrderConfig.address }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">最近设置金额</span>
                            <span className="info-value">{ lastEmptyOrderConfig.amount } USDT</span>
                        </div>
                    </div>
                ) }
                <div className="form-group">
                    <label className="form-label">用户地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ emptyOrderAddress }
                        onChange={ (e) => setEmptyOrderAddress(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">空单金额</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ emptyOrderAmount }
                        onChange={ (e) => setEmptyOrderAmount(e.target.value) }
                        placeholder="0.0"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetEmptyOrder }>
                    设置空单
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置费率阶段</h2>
                <div style={ { marginBottom: "12px" } }>
                    <div className="info-row">
                        <span className="info-label">当前费率阶段</span>
                        <span className="info-value">{ currentRateStages.length } 条</span>
                    </div>
                    { currentRateStages.length > 0 ? currentRateStages.map((stage) => (
                        <div key={ stage.index } className="info-row">
                            <span className="info-label">索引{ stage.index } / { stage.dayThreshold }天</span>
                            <span className="info-value">{ stage.hourlyRate } 基点/h (日化 { stage.dailyRate }%)</span>
                        </div>
                    )) : (
                        <div className="info-row">
                            <span className="info-label">费率阶段</span>
                            <span className="info-value">暂无数据</span>
                        </div>
                    ) }
                </div>
                <div className="form-group">
                    <label className="form-label">选择天数阶段</label>
                    <select
                        className="form-input"
                        value={ selectedRateStageIndex }
                        onChange={ (e) => {
                            const nextIndex = e.target.value;
                            setSelectedRateStageIndex(nextIndex);
                            const current = currentRateStages.find((s) => s.index === Number(nextIndex));
                            if (current) {
                                setSelectedRateDayThreshold(String(current.dayThreshold));
                                setSelectedRateCurrentDaily(String(current.dailyRate));
                                setSelectedRateNewDaily("");
                            } else {
                                setSelectedRateDayThreshold("");
                                setSelectedRateCurrentDaily("");
                                setSelectedRateNewDaily("");
                            }
                        } }
                    >
                        { currentRateStages.length > 0 ? currentRateStages.map((stage) => (
                            <option key={ stage.index } value={ String(stage.index) }>
                                阶段 { stage.index } (≤ { stage.dayThreshold } 天)
                            </option>
                        )) : (
                            <option value="">暂无可选阶段</option>
                        ) }
                    </select>
                </div>
                <div className="form-group">
                    <label className="form-label">当前阶段天数上限</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ selectedRateDayThreshold }
                        readOnly
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">当前日收益率 (%)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ selectedRateCurrentDaily }
                        readOnly
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">新的日收益率 (%)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ selectedRateNewDaily }
                        onChange={ (e) => setSelectedRateNewDaily(e.target.value) }
                        placeholder="例如: 1.20"
                    />
                </div>
                <p style={ { color: "#aaa", marginBottom: "12px", fontSize: "13px" } }>
                    天数阶段从合约读取；设置时将日收益率自动换算为小时收益率，仅提交 1 笔交易
                </p>
                <button className="btn btn-primary" onClick={ handleSetRateStage }>
                    设置当前阶段费率
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置锁仓订单 (LML)</h2>
                { lastLmlUserConfig && (
                    <div style={ { marginBottom: "12px" } }>
                        <div className="info-row">
                            <span className="info-label">最近设置地址</span>
                            <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>{ lastLmlUserConfig.address }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">最近设置金额</span>
                            <span className="info-value">{ lastLmlUserConfig.amount } USDT</span>
                        </div>
                    </div>
                ) }
                <div className="form-group">
                    <label className="form-label">用户地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ lmlUserAddress }
                        onChange={ (e) => setLmlUserAddress(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">订单金额</label>
                    <input
                        type="number"
                        className="form-input"
                        value={ lmlUserAmount }
                        onChange={ (e) => setLmlUserAmount(e.target.value) }
                        placeholder="0.0"
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleSetLMLUser }>
                    设置锁仓订单
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">设置10%入金奖励地址</h2>
                { lastSpecialRoleConfig && (
                    <div style={ { marginBottom: "12px" } }>
                        <div className="info-row">
                            <span className="info-label">最近设置地址</span>
                            <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>{ lastSpecialRoleConfig.address }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">最近设置状态</span>
                            <span className="info-value">{ lastSpecialRoleConfig.enabled ? "开启" : "关闭" }</span>
                        </div>
                    </div>
                ) }
                <div className="form-group">
                    <label className="form-label">地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ specialRoleAddress }
                        onChange={ (e) => setSpecialRoleAddress(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">是否可获得奖励</label>
                    <select
                        className="form-input"
                        value={ specialRoleEnabled }
                        onChange={ (e) => setSpecialRoleEnabled(e.target.value) }
                    >
                        <option value="true">开启</option>
                        <option value="false">关闭</option>
                    </select>
                </div>
                <button className="btn btn-primary" onClick={ handleSetSpecialRole }>
                    设置10%奖励用户
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">价格快照</h2>
                <p style={ { fontSize: "14px", color: "#aaa", marginBottom: "16px" } }>
                    触发价格快照以记录当前代币价格和BNB价格
                </p>
                <button className="btn btn-primary" onClick={ handleTriggerSnapshot }>
                    触发价格快照
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">权限管理</h2>
                <div className="info-row">
                    <span className="info-label">当前管理员</span>
                    <span className="info-value">{ adminList.length } 个</span>
                </div>
                <div style={ { marginBottom: "12px" } }>
                    { adminList.length > 0 ? adminList.map((admin) => (
                        <div key={ admin } className="info-row">
                            <span className="info-label">管理员</span>
                            <span className="info-value" style={ { fontFamily: "monospace", fontSize: "12px" } }>{ admin }</span>
                        </div>
                    )) : (
                        <div className="info-row">
                            <span className="info-label">管理员</span>
                            <span className="info-value">暂无数据</span>
                        </div>
                    ) }
                </div>
                <div className="form-group">
                    <label className="form-label">新管理员地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ newAdminAddress }
                        onChange={ (e) => setNewAdminAddress(e.target.value) }
                        placeholder="0x..."
                    />
                </div>
                <button className="btn btn-primary" onClick={ handleTransferAdmin } style={ { marginBottom: "16px" } }>
                    转移管理员权限
                </button>
                <div>
                    <button className="btn btn-danger" onClick={ handleRenounceOwnership }>
                        丢弃所有权限（不可逆）
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MiningAdmin;
